import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TourService } from '../../../core/tour/tour.service';
import { TranslateMarkdownPipe } from '../../pipes/translate-markdown.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Gap between the highlighted element and the highlight ring, in px. */
const HIGHLIGHT_PADDING = 6;
/** How often the anchor is re-measured (pages render async, layouts shift). */
const MEASURE_INTERVAL_MS = 250;

/**
 * Renders the active guided tour (TourService) over the app: a highlight ring
 * (optionally dimming everything else) around the step's anchor element, and a
 * step card docked at the top or bottom of the screen, away from the anchor.
 *
 * The overlay never captures clicks outside the card, so the user can keep
 * using the real page under it; that's how "do it yourself" steps work.
 */
@Component({
  selector: 'app-tour-overlay',
  imports: [ButtonComponent, IconComponent, TranslatePipe, TranslateMarkdownPipe],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourOverlayComponent {
  protected tour = inject(TourService);
  private document = inject(DOCUMENT);

  protected anchorRect = signal<AnchorRect | null>(null);
  protected busy = signal(false);
  private scrolledForStep: string | null = null;

  protected step = this.tour.step;
  protected progressParams = computed(() => ({
    step: String(this.tour.stepIndex() + 1),
    total: String(this.tour.stepCount()),
  }));

  /** Dock the card on the opposite half of the screen from the anchor. */
  protected cardPlacement = computed<'top' | 'bottom' | 'center'>(() => {
    const rect = this.anchorRect();
    if (!rect) return 'center';
    const viewportHeight = this.document.defaultView?.innerHeight ?? 0;
    return rect.top + rect.height / 2 > viewportHeight / 2 ? 'top' : 'bottom';
  });

  protected highlight = computed(() => {
    const rect = this.anchorRect();
    if (!rect) return null;
    return {
      top: rect.top - HIGHLIGHT_PADDING,
      left: rect.left - HIGHLIGHT_PADDING,
      width: rect.width + HIGHLIGHT_PADDING * 2,
      height: rect.height + HIGHLIGHT_PADDING * 2,
      spotlight: this.step()?.spotlight !== false,
    };
  });

  constructor() {
    const timer = setInterval(() => this.measure(), MEASURE_INTERVAL_MS);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
    // Re-measure immediately on step changes rather than waiting for the timer.
    effect(() => {
      this.step();
      this.tour.minimized();
      this.measure();
    });
  }

  protected async primary(): Promise<void> {
    await this.run(() => this.tour.next());
  }

  protected async back(): Promise<void> {
    await this.run(() => this.tour.back());
  }

  protected async action(): Promise<void> {
    const action = this.step()?.action;
    if (action) await this.run(async () => action.run());
  }

  private async run(fn: () => Promise<void> | void): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      await fn();
    } finally {
      this.busy.set(false);
    }
  }

  /** Locates the step's anchor element and tracks its on-screen position. */
  measure(): void {
    const step = this.step();
    if (!step?.anchor || this.tour.minimized()) {
      this.setRect(null);
      return;
    }
    const el = this.document.querySelector(`[data-testid="${CSS.escape(step.anchor)}"]`);
    const r = el?.getBoundingClientRect();
    if (!el || !r || (r.width === 0 && r.height === 0)) {
      this.setRect(null);
      return;
    }
    if (this.scrolledForStep !== step.id) {
      this.scrolledForStep = step.id;
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
    this.setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }

  private setRect(next: AnchorRect | null): void {
    const prev = this.anchorRect();
    const same =
      prev === next ||
      (!!prev &&
        !!next &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height);
    if (!same) this.anchorRect.set(next);
  }
}
